import { useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, Grid , TablePagination
} from '@mui/material';
import { scaleLinear } from 'd3-scale';


const timePeriods = ['1mo', '3mo', '6mo', '1y'];

const colorScale = scaleLinear()
  .domain([-35, 0, 45])
  .range(['#ff4d4d', '#ffffff', '#00cc44']);

const Heatmap = ({ data }) => {
  const processedData = data.map((stock) => ({
    name: stock.symbol,
    returns: timePeriods.map(period => stock.periods[period] ?? 0), // default to 0 if missing
  }));

  const [page, setPage] = useState(0);
  const rowsPerPage = 15;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedData = processedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const bestPerformers = timePeriods.map((_, colIdx) => {
    const best = processedData.reduce((prev, curr) =>
      curr.returns[colIdx] > prev.returns[colIdx] ? curr : prev
    );
    return {
      name: best.name,
      value: best.returns[colIdx].toFixed(2),
      period: timePeriods[colIdx]
    };
  });

  const worstPerformers = timePeriods.map((_, colIdx) => {
    const worst = processedData.reduce((prev, curr) =>
      curr.returns[colIdx] < prev.returns[colIdx] ? curr : prev
    );
    return {
      name: worst.name,
      value: worst.returns[colIdx].toFixed(2),
      period: timePeriods[colIdx]
    };
  });

  return (
    <Paper sx={{ padding: 4, backgroundColor: 'white', color: '#2d4890' }}>
      <Typography variant="h5" gutterBottom>
        📈 Returns Heatmap by Time Period
      </Typography>

      {/* Heatmap Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#2d4890' }}>Stock</TableCell>
              {timePeriods.map((label) => (
                <TableCell key={label} sx={{ color: '#2d4890' }}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((stock) => (
              <TableRow key={stock.name}>
                <TableCell sx={{ color: '#2d4890' }}>{stock.name}</TableCell>
                {stock.returns.map((value, idx) => (
                  <TableCell
                    key={idx}
                    sx={{
                      backgroundColor: colorScale(value),
                      color: '#000',
                      textAlign: 'center',
                    }}
                    title={`${value.toFixed(2)}%`}
                  >
                    {value.toFixed(1)}%
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="flex-end" px={2}>
        <TablePagination
          component="div"
          count={processedData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={() => { }}
          rowsPerPageOptions={[]} // Hides rows-per-page dropdown
          sx={{
            color: '#2d4890',
            '& .MuiTablePagination-toolbar': {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center', // Align text and buttons vertically
              justifyContent: 'flex-start', // Align everything to the left
              paddingLeft: 0,
            },
            '& .MuiTablePagination-actions button': {
              color: '#2d4890',
            },
          }}
        />
      </Box>

      {/* Info Panel */}
      <Box mt={4} p={2} bgcolor="#2d4890" borderRadius={2} color="white">
        <Typography variant="h6" gutterBottom>
          How to interpret this heatmap:
        </Typography>
        <Typography variant="body2" gutterBottom>
          This visualization shows the percentage returns of each stock in your portfolio over different time periods.
        </Typography>
        <ul>
          <li><Typography variant="body2"> <b>Blue</b>: Positive returns (darker = higher)</Typography></li>
          <li><Typography variant="body2"> <b>Red</b>: Negative returns (darker = lower)</Typography></li>
          <li><Typography variant="body2"> <b>White</b>: Returns near 0</Typography></li>
        </ul>
        <Typography variant="body2">Hover over each cell to see the exact percentage return.</Typography>
      </Box>

      {/* Best and Worst Performers */}
      <Grid container spacing={3} mt={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6">Best Performers</Typography>
          {bestPerformers.map((entry) => (
            <Box key={entry.period} mt={1}>
              <Typography variant="body2">
                <strong>Best ({entry.period})</strong>: {entry.name} <span style={{ color: '#00cc66' }}>↑ {entry.value}%</span>
              </Typography>
            </Box>
          ))}
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h6">Worst Performers</Typography>
          {worstPerformers.map((entry) => (
            <Box key={entry.period} mt={1}>
              <Typography variant="body2">
                <strong>Worst ({entry.period})</strong>: {entry.name} <span style={{ color: '#ff3333' }}>↓ {entry.value}%</span>
              </Typography>
            </Box>
          ))}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default Heatmap;
